/**
 * Club 15 (Al Quoz): a brand on top of the DFC Fitness Village design, built the way All Sports
 * Arena is (`asaBrand.ts`).
 *
 * Club 15's company record carries `theme: 'club15Theme'`. `useCompany` hands the rest of the
 * app `theme: 'dfcVillageTheme'` plus `brandVariant: 'club15'` (see `aliasClub15Theme`). Club 15
 * gets every ASA behaviour (`usesAsaFlows`) and its own brand layer:
 *   palette  CLUB15_PALETTE, written to the `--village-*` properties `DFC_PALETTE` falls back
 *            from, per light / dark mode; CLUB15_SURFACE for surfaces, ink and buttons
 *   type     Archivo, with Playfair Display italic for the emphasis word, as club15.ae
 *   copy     CLUB15_COPY_EN, returned by `useFitnessVillageCopy`; CLUB15_HOME_COPY for the hero
 *   artwork  `public/club15/`, from club15.ae (CLUB15_ART)
 *
 * Colours, type and words are club15.ae's own (read 21/10). Nothing here states a price, a
 * count or a rule: those come from the catalogue.
 */
import type { Company } from '../hooks/useCompany';
import { BrandPalette, PALETTE_CSS_VAR, hexToRgbChannels, withAlpha } from './dfcBrand';
import { DfcSurface } from './dfcSurface';
import { VILLAGE_MODE_ATTRIBUTE } from '../context/DfcThemeModeContext';
import { FITNESS_VILLAGE_COPY_EN, FitnessVillageCopy } from './fitnessVillageCopy';
import { isAsaCompany } from './asaBrand';

export const CLUB15_THEME = 'club15Theme';

export const isClub15Company = (company?: Pick<Company, 'brandVariant'> | null): boolean =>
  company?.brandVariant === 'club15';

/**
 * True for the companies that run ASA's flows: court flow with durations and cart, gym tab,
 * bookable class rows, vouchers and memberships, the services home. ASA and Club 15.
 */
export const usesAsaFlows = (company?: Pick<Company, 'brandVariant'> | null): boolean =>
  isAsaCompany(company) || isClub15Company(company);

/** Club 15's record as the app should see it: the DFC village theme, flagged. Others pass through. */
export const aliasClub15Theme = (company: Company): Company =>
  (company.theme as string) === CLUB15_THEME
    ? { ...company, theme: 'dfcVillageTheme', brandVariant: 'club15' }
    : company;

/** The `:root` palette of club15.ae (21/10). */
export const CLUB15_COLOR = {
  coral: '#A54D3F',
  charcoal: '#262626',
  cream: '#F4E9DC',
  sand: '#D3C0AB',
  navy: '#263751',
  sage: '#7D8E8B',
  sky: '#98AAB3',
  burgundy: '#561B1B',
  white: '#FFFFFF',
  /** The soft fill (notes, hover) on the cream page: coral on it 5.29:1, charcoal 14.2:1. */
  paper: '#FBF7F1',
  /** Secondary text on cream / white: 6.00:1 / 7.18:1 (the shared grey #666D80 is 4.3:1 on cream). */
  inkSoftOnLight: '#5E5650',
  /** Muted text on cream / white: 5.29:1 / 6.33:1 (the shared grey #A4ABB8 is 2.0:1). */
  mutedOnLight: '#675E57',
  /** Error text on cream: 6.11:1 (DFC's #C82E34 is 4.50:1, on the line). Also the error toast fill. */
  errorOnLight: '#A3262B',
  /** Error text on charcoal: 7.55:1 (DFC's red is 2.8:1 there). */
  errorOnDark: '#F2A39A',
} as const;

/*
 * Contrast (WCAG 2.1):
 *   coral on cream 4.70:1, cream on coral 4.70:1, coral on white 5.63:1   AA, any size
 *   charcoal on cream 12.64:1, charcoal on white 15.13:1                    AA, any size
 *   sand on charcoal 8.57:1, charcoal on sand 8.57:1                        AA, any size
 *   cream on charcoal 12.64:1, sky on charcoal 6.30:1                       AA, any size
 *   coral on charcoal 2.69:1                                                FAILS
 *   sage on cream 2.87:1                                                    FAILS
 * So coral never carries text or a mark in dark mode (accents turn sand there), and sage is not
 * used for text at all.
 */

/**
 * Club 15's palette per mode, in DFC's slots: blue = primary accent, green = secondary accent
 * (underlines, badges, focus), black = ink and the dark page, white = paper and ink on fills.
 * blueDark / greenDark are gradient end stops only.
 */
export const CLUB15_PALETTE: Record<'light' | 'dark', BrandPalette> = {
  light: {
    blue: CLUB15_COLOR.coral,
    green: CLUB15_COLOR.coral,
    black: CLUB15_COLOR.charcoal,
    white: CLUB15_COLOR.cream,
    blueDark: CLUB15_COLOR.burgundy,
    greenDark: CLUB15_COLOR.burgundy,
  },
  dark: {
    blue: CLUB15_COLOR.sand,
    green: CLUB15_COLOR.sand,
    black: CLUB15_COLOR.charcoal,
    white: CLUB15_COLOR.cream,
    blueDark: CLUB15_COLOR.sky,
    greenDark: CLUB15_COLOR.sky,
  },
};

/**
 * Club 15's overrides of the village surface scale (see `useDfcSurface`). Light: a cream page,
 * white cards, charcoal ink, coral buttons with cream text. Dark: charcoal, cream ink, sand
 * buttons with charcoal text.
 */
export const CLUB15_SURFACE: Record<'light' | 'dark', Partial<DfcSurface>> = {
  light: {
    pageBg: CLUB15_COLOR.cream,
    surface: CLUB15_COLOR.white,
    soft: CLUB15_COLOR.paper,
    track: withAlpha(CLUB15_COLOR.sand, 0.45),
    border: withAlpha(CLUB15_COLOR.charcoal, 0.14),
    borderStrong: withAlpha(CLUB15_COLOR.charcoal, 0.34),
    headerBorder: withAlpha(CLUB15_COLOR.charcoal, 0.08),
    ink2: CLUB15_COLOR.inkSoftOnLight,
    ink3: CLUB15_COLOR.mutedOnLight,
    accentText: CLUB15_COLOR.coral,
    accentMark: CLUB15_COLOR.coral,
    ctaBg: CLUB15_COLOR.coral,
    ctaFg: CLUB15_COLOR.cream,
    ctaSub: CLUB15_COLOR.cream,
    ctaArrow: CLUB15_COLOR.cream,
    onBg: CLUB15_COLOR.charcoal,
    onFg: CLUB15_COLOR.cream,
    logoBg: 'transparent',
    logoPad: '0px',
    scrim: CLUB15_COLOR.charcoal,
    panel: CLUB15_COLOR.white,
  },
  dark: {
    ink3: CLUB15_COLOR.sky,
    border: withAlpha(CLUB15_COLOR.cream, 0.22),
    borderStrong: withAlpha(CLUB15_COLOR.cream, 0.45),
    accentText: CLUB15_COLOR.sand,
    accentMark: CLUB15_COLOR.sand,
    ctaBg: CLUB15_COLOR.sand,
    ctaFg: CLUB15_COLOR.charcoal,
    ctaSub: CLUB15_COLOR.charcoal,
    ctaArrow: CLUB15_COLOR.charcoal,
    onBg: CLUB15_COLOR.sand,
    onFg: CLUB15_COLOR.charcoal,
    logoBg: 'transparent',
    logoPad: '0px',
    scrim: CLUB15_COLOR.charcoal,
  },
};

/** Club 15's artwork, bundled from club15.ae/assets. Served from `public/club15/`. */
const ART = `${process.env.PUBLIC_URL || ''}/club15`;
export const CLUB15_ART = {
  /** For dark surfaces (club15.ae's own header logo) */
  logoCream: `${ART}/logo-cream.png`,
  /** For light surfaces */
  logoCharcoal: `${ART}/logo-charcoal.png`,
  /** The first frame of club15.ae's hero film */
  hero: `${ART}/club15-descent-v53-start.jpg`,
  exterior: `${ART}/exterior.jpg`,
  padel: `${ART}/padel-courts.jpg`,
  basketball: `${ART}/basketball-court.jpg`,
  pool: `${ART}/pool-hd.jpg`,
  gym: `${ART}/gym-two-floor-hd.jpg`,
  reformer: `${ART}/studio-reformer.jpg`,
  recovery: `${ART}/recovery-sauna.jpg`,
} as const;

/**
 * The render that fits a service, by its name, for a service that has no image of its own. A
 * service's own image always wins. Undefined when nothing fits.
 */
export const club15ServiceArt = (serviceName: string): string | undefined => {
  if (/padel/i.test(serviceName)) return CLUB15_ART.padel;
  if (/basket/i.test(serviceName)) return CLUB15_ART.basketball;
  if (/pool|swim|lane/i.test(serviceName)) return CLUB15_ART.pool;
  if (/gym|strength|hiit|boxing/i.test(serviceName)) return CLUB15_ART.gym;
  if (/reformer|pilates|yoga|studio/i.test(serviceName)) return CLUB15_ART.reformer;
  if (/recover|sauna|ice/i.test(serviceName)) return CLUB15_ART.recovery;
  return undefined;
};

/** Class name of the emphasis word (Playfair Display italic). See `Club15Word`. */
export const CLUB15_EMPHASIS_CLASS = 'club15-em';

const CLUB15_STYLE_ID = 'club15-brand-palette';
const CLUB15_FONTS_ID = 'club15-brand-fonts';
/** As club15.ae loads them. */
const CLUB15_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Archivo:wght@300;600;800&family=Playfair+Display:ital,wght@1,700&display=swap';
const SANS = "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif";
const SERIF = "'Playfair Display', Georgia, serif";

const paletteRule = (selector: string, palette: BrandPalette) =>
  `${selector}{${(Object.keys(PALETTE_CSS_VAR) as (keyof BrandPalette)[])
    .map(
      key =>
        `${PALETTE_CSS_VAR[key]}:${palette[key]};${PALETTE_CSS_VAR[key]}-rgb:${hexToRgbChannels(palette[key])};`
    )
    .join('')}}`;

const DARK = `:root[${VILLAGE_MODE_ATTRIBUTE}='dark']`;

/**
 * Type: the village styles name Inter, Dubai or Roboto on their own roots, hence the universal
 * selector (as the Arabic override in `dfcDirection.tsx`). Exists only while the company is Club 15.
 */
const typeCss = (): string =>
  [
    `body,body *{font-family:${SANS} !important;}`,
    `body .${CLUB15_EMPHASIS_CLASS}{font-family:${SERIF} !important;font-style:italic;font-weight:700;text-transform:none;letter-spacing:0;}`,
  ].join('');

/**
 * Club 15 rules for shared pieces the village surface scale does not reach: the promo-code
 * field, react-toastify's toasts, the cart countdown strip and ASA's credit chip.
 */
const purchaseFlowCss = (): string => {
  const { coral, cream, charcoal, sand, errorOnLight } = CLUB15_COLOR;
  return [
    `.package-code-input .MuiOutlinedInput-input::placeholder{color:${CLUB15_COLOR.mutedOnLight};opacity:1;}`,
    `.package-code-input .package-code-button{background:${coral};color:${cream};border-radius:999px;padding:4px 16px;}`,
    `.package-code-input .package-code-button.Mui-disabled{background:${withAlpha(coral, 0.45)};color:${cream};}`,
    `${DARK} .package-code-input{color:${cream};background:${withAlpha(cream, 0.06)};}`,
    `${DARK} .package-code-input .MuiOutlinedInput-notchedOutline{border-color:${withAlpha(cream, 0.5)};}`,
    `${DARK} .package-code-input .MuiOutlinedInput-input::placeholder{color:${CLUB15_COLOR.sky};}`,
    `${DARK} .package-code-input .package-code-button{background:${sand};color:${charcoal};}`,
    // Toasts: charcoal success and a deep red error, both under cream text (12.64:1, 6.11:1).
    `:root:root{--toastify-color-success:${charcoal};--toastify-color-error:${errorOnLight};}`,
    `${DARK}{color-scheme:dark;}`,
    // Cart countdown: a coral band with cream text (4.70:1); sand with charcoal in dark mode.
    `[data-testid='cart-hold-strip']{background:${coral};}`,
    `[data-testid='cart-hold-strip'] span{color:${cream};}`,
    `${DARK} [data-testid='cart-hold-strip']{background:${sand};border-color:${charcoal};}`,
    `${DARK} [data-testid='cart-hold-strip'] span{color:${charcoal};}`,
    // ASA's "AED 50 extra" chip (`ExtraChip`) reads these.
    `:root{--club15-chip-bg:${coral};--club15-chip-fg:${cream};}`,
    `${DARK}{--club15-chip-bg:${sand};--club15-chip-fg:${charcoal};}`,
  ].join('');
};

export const club15PaletteCss = (): string =>
  `:root{--village-on-photo:${CLUB15_COLOR.cream};--village-on-accent:${CLUB15_COLOR.cream};--village-error:${CLUB15_COLOR.errorOnLight};--village-error-rgb:${hexToRgbChannels(CLUB15_COLOR.errorOnLight)};}` +
  `${DARK}{--village-on-accent:${CLUB15_COLOR.charcoal};--village-error:${CLUB15_COLOR.errorOnDark};--village-error-rgb:${hexToRgbChannels(CLUB15_COLOR.errorOnDark)};}` +
  paletteRule(':root', CLUB15_PALETTE.light) +
  paletteRule(DARK, CLUB15_PALETTE.dark) +
  typeCss() +
  purchaseFlowCss();

/*
 * The loader has to be chosen before the company record (and so its theme) has loaded. Club
 * 15's known company ids, plus any id this browser has already seen load as Club 15, are the
 * hint. It only picks the loader: every Club 15 behaviour still keys off the theme.
 */
export const CLUB15_KNOWN_COMPANY_IDS: string[] = [
  'd1e7076b6565aadd51afee726e2d8cd2', // production (no staging record yet)
];
const CLUB15_SEEN_IDS_KEY = 'club15CompanyIds';

const readSeenClub15Ids = (): string[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(CLUB15_SEEN_IDS_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const isLikelyClub15CompanyId = (companyId?: string | null): boolean =>
  Boolean(companyId) &&
  (CLUB15_KNOWN_COMPANY_IDS.includes(companyId as string) ||
    readSeenClub15Ids().includes(companyId as string));

const rememberClub15CompanyId = (companyId?: string) => {
  if (!companyId || isLikelyClub15CompanyId(companyId)) return;
  try {
    window.localStorage.setItem(
      CLUB15_SEEN_IDS_KEY,
      JSON.stringify([...readSeenClub15Ids(), companyId])
    );
  } catch {
    // Storage unavailable: the next visit shows the generic loader, nothing more.
  }
};

const BRAND = 'Club 15';

export const CLUB15_LOADER_LABEL = `Loading ${BRAND}`;

/**
 * Adds (Club 15) or removes (everyone else) the Club 15 stylesheet and its Google Fonts link.
 * Without them every `DFC_PALETTE` value resolves to its DFC hex, so for DFC and ASA this
 * changes nothing.
 */
export const applyClub15Brand = (
  company?: (Pick<Company, 'brandVariant'> & { id?: string }) | null
) => {
  const existing = document.getElementById(CLUB15_STYLE_ID);
  const fonts = document.getElementById(CLUB15_FONTS_ID);
  if (!isClub15Company(company)) {
    existing?.remove();
    fonts?.remove();
    return;
  }
  rememberClub15CompanyId(company?.id);
  if (!fonts) {
    const link = document.createElement('link');
    link.id = CLUB15_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = CLUB15_FONTS_URL;
    document.head.appendChild(link);
  }
  const style = existing ?? document.head.appendChild(document.createElement('style'));
  style.id = CLUB15_STYLE_ID;
  style.textContent = club15PaletteCss();
};

const DFC = FITNESS_VILLAGE_COPY_EN;

/** Club 15's English, in the shape of `ASA_COPY_EN`. Only what names DFC, its event or its rules is replaced. */
export const CLUB15_COPY_EN: FitnessVillageCopy = {
  ...DFC,
  home: {
    ...DFC.home,
    dateline: BRAND,
    /** Not drawn: the Club 15 home has its own hero (`Club15Hero`). Set so no DFC name is inherited. */
    heroTitle: { lead: 'The Standard', tail: 'is 15.' },
    heroLead: 'The Standard',
    heroAccent: 'is 15.',
    intro: 'Book a court, a class or the gym.',
    chooseVillage: 'Choose a venue',
    classesAction: { ...DFC.home.classesAction, sub: 'See the timetable' },
    courtsAction: {
      label: 'Courts',
      sub: 'Book a slot',
      aria: (venue: string) => `Book a court at ${venue}`,
    },
    emptyVillages: 'Booking opens soon.',
    stats: { title: BRAND, footnote: '' },
    lockups: { title: 'Venues', alt: (venue: string) => venue },
  },
  homeStats: [],
  page: {
    ...DFC.page,
    // One club, so the back link goes home rather than to a list of venues.
    allVillages: 'Home',
    switchVillage: 'Switch venue',
    classesIntro: 'Pick a class and book your spot.',
    zones: 'Classes',
    seeAllZones: (count: number) => `See all ${count} classes`,
    classesNote: 'Places are limited, so book ahead.',
    courtsLead: 'Book a court at',
    tabs: { classes: 'Classes', courts: 'Courts', ariaLabel: 'Classes or courts' },
    sports: 'Courts',
    seeAllSports: (count: number) => `See all ${count} courts`,
    courtsIntro: 'Pick a court, a day and a time.',
    courtHours: (hours: string) => `Bookings: ${hours}`,
    loadError: 'We could not load the club. Try again.',
    mapComingSoon: 'Club map coming soon.',
    villageTitle: (venue: string) => venue,
    unknownVillage: 'We could not find this venue.',
    classDetails: { ...DFC.page.classDetails, dropIn: 'Tap the class to book your spot.' },
    alreadyBooked: (time: string) => `${time}, already booked`,
  },
  auth: {
    ...DFC.auth,
    eyebrow: BRAND,
    signUp: { ...DFC.auth.signUp, body: 'One account for all your bookings.' },
  },
  header: {
    ...DFC.header,
    logoAlt: BRAND,
    home: `${BRAND} home`,
    menuFootnote: BRAND,
    nav: { ...DFC.header.nav, bookings: 'Courts' },
  },
  checkout: {
    ...DFC.checkout,
    backToVillages: 'Home',
    emptyCartAction: 'Browse classes and courts',
  },
  confirmation: { ...DFC.confirmation, backHome: 'Home' },
  profile: {
    ...DFC.profile,
    checkInBody: 'Show this code at reception.',
    empty: 'Not set',
    browseVillages: 'Browse classes and courts',
  },
  signup: { ...DFC.signup, eyebrow: BRAND },
  cancellation: { ...DFC.cancellation, backHome: 'Home' },
  loader: { label: CLUB15_LOADER_LABEL },
};

/**
 * The Club 15 home hero (`Club15Hero`), in club15.ae's words. The rest of the home (coming up,
 * sign-in, the photo cards) uses ASA's labels, which name no brand.
 */
export const CLUB15_HOME_COPY = {
  eyebrow: 'Al Quoz · Dubai',
  hours: 'Open 06:00 to 23:00',
  /** "THE STANDARD / IS 15." with "15." in the emphasis face */
  heroLines: ['The Standard', 'is'],
  heroWord: '15.',
  /** "15,000 sqm of sport, ..." with the figure in bold, as club15.ae */
  tagLead: '15,000 sqm',
  tagRest:
    'of sport, performance, recovery and lifestyle. All in one destination in Al Quoz, Dubai.',
  bookSpace: 'Book a court',
  findClass: 'Find a class',
};

/**
 * Headings and tab titles of Club 15's venue page, from club15.ae's own lines. The word takes
 * the emphasis face.
 */
export const CLUB15_PAGE_HEADINGS = {
  classes: { lead: 'Push yourself', word: 'further.', tail: '', title: 'Classes' },
  courts: { lead: 'Built to a different', word: 'standard.', tail: '', title: 'Courts' },
  gym: { lead: 'Get ready for', word: 'what’s ahead.', tail: '', title: 'Gym' },
};
